import { useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";

// Tipos
type AdicionarColaboradorProps = {
  onSave: (obj: {
    nomeUsuario: string;
    email: string;
    tipo: string;
    login: string;
    perfilAcesso: string;
  }) => Promise<any>;
  onCancel: () => void;
};

// Componente
const AdicionarColaborador = ({ onSave, onCancel }: AdicionarColaboradorProps) => {
  const [val, setVal] = useState({
    nomeUsuario: "",
    email: "",
    login: "",          
    perfilAcesso: "",
    tipo: "",           // "GP" | "TERCEIRO"
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const ApenasNumber = (value: string) => value.replace(/\D/g, "");

  const handleSubmit = async () => {
    if (
      !val.nomeUsuario.trim() ||
      !val.email.trim() ||
      !val.perfilAcesso ||
      !val.tipo
    ) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }

    // validação da matrícula (somente a parte numérica)
    if (val.login.length < 8) {
      setError("A matrícula deve conter pelo menos 8 dígitos.");
      return;
    }

    setError("");
    setLoading(true);
    try {
      const loginFinal = val.tipo === "TERCEIRO" ? `C${val.login}` : val.login;

      const result = await onSave({
        nomeUsuario: val.nomeUsuario.trim(),
        email: val.email.trim(),
        tipo: val.tipo || "GP",
        login: loginFinal,
        perfilAcesso: val.perfilAcesso,
      });

      if (result.success) {
        toast.success("Colaborador adicionado com sucesso!");
        setVal({ nomeUsuario: "", email: "", login: "", perfilAcesso: "", tipo: "" });
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      toast.error("Erro inesperado ao salvar colaborador.");
    } finally {
      setLoading(false);
    }
  };

  // valor exibido no input de matrícula (prefixa C somente para TERCEIRO)
  const loginDisplay = val.tipo === "TERCEIRO" && val.login ? `C${val.login}` : val.login;

  return (
    <Overlay>
      <ModalContainer>
        <Title>Adicionar novo Colaborador</Title>

        <InputGroup>
          <Input
            type="text"
            value={val.nomeUsuario}
            onChange={(e) =>
              setVal((prev) => ({ ...prev, nomeUsuario: e.target.value }))
            }
            placeholder="Nome do usuário"
            disabled={loading}
          />
          <Label>Nome de Usuário</Label>
        </InputGroup>

        <InputGroup>
          <Input
            type="text"
            value={val.email}
            onChange={(e) =>
              setVal((prev) => ({ ...prev, email: e.target.value }))
            }
            placeholder="E-mail"
            disabled={loading}
          />
          <Label>E-mail</Label>
        </InputGroup>

        <InputGroup>
          <Label>Tipo de Colaborador</Label>
          <select
            style={{
              width: "100%",
              padding: "10px",
              borderRadius: "6px",
              border: "1px solid #cfd8dc",
              outline: "none",
            }}
            value={val.tipo}
            onChange={(e) =>
              setVal((prev) => ({
                ...prev,
                tipo: e.target.value,
                // opcional: limpa a matrícula ao mudar o tipo
                login: "",
              }))
            }
            disabled={loading}
          >
            <option value="">Selecione</option>
            <option value="GP">Colaborador</option>
            <option value="TERCEIRO">Terceiro</option>
          </select>
        </InputGroup>

        <InputGroup>
          <Input
            type="text"
            value={loginDisplay}
            // TERCEIRO: 1 (C) + 8 dígitos = 9; Normal: até 8 dígitos
            maxLength={val.tipo === "TERCEIRO" ? 9 : 8}
            onChange={(e) =>
              setVal((prev) => ({
                ...prev,
                // remove um "C" inicial (caso usuário cole "C123...") e limpa para apenas números
                login: ApenasNumber(e.target.value.replace(/^C/i, "")),
              }))
            }
            placeholder={val.tipo === "TERCEIRO" ? "C + matrícula (somente números)" : "Matrícula (somente números)"}
            disabled={loading}
          />
          <Label>Matrícula</Label>
          {error && <ErrorMessage>{error}</ErrorMessage>}
        </InputGroup>

        <InputGroup>
          <Label>Perfil de acesso</Label>
          <select
            style={{
              width: "100%",
              padding: "10px",
              borderRadius: "6px",
              border: "1px solid #cfd8dc",
              outline: "none",
            }}
            value={val.perfilAcesso}
            onChange={(e) =>
              setVal((prev) => ({
                ...prev,
                perfilAcesso: e.target.value,
              }))
            }
            disabled={loading}
          >
            <option value="">Selecione</option>
            <option value="usuarioColaborador">Usuário Colaborador</option>
            <option value="adminUnidade">Administrador da Unidade</option>
            <option value="superAdmin">Administrador Geral</option>
          </select>
        </InputGroup>

        <ButtonGroup>
          <ButtonCancelar onClick={onCancel} disabled={loading}>
            Voltar
          </ButtonCancelar>
          <ButtonEnviar onClick={handleSubmit} disabled={loading}>
            {loading ? "Enviando..." : "Enviar"}
          </ButtonEnviar>
        </ButtonGroup>
      </ModalContainer>
    </Overlay>
  );
};

export default AdicionarColaborador;

// === styled-components ===

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(20, 25, 41, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;

const ModalContainer = styled.div`
  background: white;
  width: 400px;
  padding: 30px 28px;
  border-radius: 8px;
  box-shadow: 0 0 12px rgba(0, 0, 0, 0.14);
`;

const Title = styled.h2`
  font-size: 22px;
  color: #2c3e50;
  margin-bottom: 30px;
  text-align: center;
`;

const InputGroup = styled.div`
  position: relative;
  margin-bottom: 24px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  font-size: 16px;
  border: 1.8px solid #cfd8dc;
  border-radius: 6px;
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: #007bff;
  }
`;

const Label = styled.label`
  position: absolute;
  left: 14px;
  top: -10px;
  background: white;
  padding: 0 6px;
  font-size: 13px;
  color: #607d8b;
  pointer-events: none;
`;

const ErrorMessage = styled.div`
  color: red;
  font-size: 13px;
  margin-top: 4px;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
`;

const ButtonBase = styled.button`
  flex: 1;
  padding: 12px 0;
  font-size: 16px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  transition: background-color 0.2s;
  border: none;
  margin: 0 8px;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ButtonEnviar = styled(ButtonBase)`
  background-color: rgb(183, 152, 97);
  color: white;

  &:hover:not(:disabled) {
    background-color: rgba(99, 68, 15, 0.747);
  }
`;

const ButtonCancelar = styled(ButtonBase)`
  background-color: #e0e0e0;
  color: #444;

  &:hover:not(:disabled) {
    background-color: #bdbdbd;
  }
`;
